import { InMemoryCache, isReference, makeVar, 
// eslint-disable-next-line no-restricted-imports
useQuery, useReactiveVar, } from '@apollo/client';
import { availableAvatars, } from 'bank-common';
import { invariantValue, notEmpty, traceIdHeader } from 'folio-common-utils';
import { colors } from 'folio-design-tokens';
import { formatDateGroupHeader } from './formatters';
export * from './formatters';
export * from './orgs';
const expandedEvents = makeVar(new Set());
export function useExpandedEvents() {
    return useReactiveVar(expandedEvents);
}
export function expandEvent(eventFid) {
    const set = new Set(expandedEvents());
    set.add(eventFid);
    expandedEvents(set);
}
export function flipExpandedEvent(eventFid) {
    // create a new set so reference changes
    const set = new Set(expandedEvents());
    if (set.has(eventFid)) {
        set.delete(eventFid);
    }
    else {
        set.add(eventFid);
    }
    expandedEvents(set);
}
const globalError = makeVar(null);
export function useGlobalError() {
    return useReactiveVar(globalError);
}
export function setGlobalError(error) {
    globalError(error);
}
const typePolicies = {
    Account: {
        keyFields: ['accountNumber'],
        fields: { cards: { merge: false } },
    },
    Agent: {
        keyFields: ['fid'],
        fields: {
            signingJobs: { merge: false },
            activeHints: { merge: false },
        },
    },
    // @ts-expect-error: This is a nested key: https://www.apollographql.com/docs/react/caching/cache-configuration/#customizing-cache-ids
    EmployeeWithCard: { keyFields: ['agent', ['fid']] },
    FreeAgent: { keyFields: ['fid'] },
    Event: {
        keyFields: ['fid'],
        fields: {
            receipts: { merge: false },
            requirements: { merge: true },
        },
    },
    EventTransaction: { keyFields: ['fid'] },
    Receipt: { keyFields: ['fid'] },
    Invoice: { keyFields: ['fid'] },
    UploadInfo: { keyFields: ['receiptFid'] },
    CardActivity: { keyFields: ['fid'] },
    Annotation: { keyFields: ['fid'] },
    Card: { keyFields: ['fid'] },
    CardAccount: { keyFields: ['accountNumber'] },
    LedgerCategory: { keyFields: ['fid'] },
    LedgerCategoryItem: { keyFields: ['fid'] },
    Merchant: { keyFields: ['fid'] },
    Organization: {
        keyFields: ['orgNum'],
        fields: {
            employeesWithCards(existingEmployees, { canRead }) {
                if (!existingEmployees) {
                    return existingEmployees;
                }
                return existingEmployees.filter(canRead);
            },
            invitations(existingInvitations, { canRead }) {
                if (!existingInvitations) {
                    return existingInvitations;
                }
                return existingInvitations.filter(canRead);
            },
        },
    },
    PaymentInfo: { keyFields: ['paymentFid'] },
    PayReply: { keyFields: ['paymentFid'] },
    PeriodExportableStatus: { keyFields: ['startDate', 'endDate'], merge: false },
    OnboardingOwnerOutput: { keyFields: ['pNum'] },
    OrgOnboardingProgressState: { keyFields: ['orgNumber'] },
    OcrAccount: { keyFields: ['accountNumber'] },
    OwnFoundingReply: { keyFields: ['foundingId'] },
    AddressBookContact: { keyFields: ['fid'] },
    Earmark: { keyFields: ['fid'] },
    Promotion: { keyFields: ['fid'] },
    Invitation: { keyFields: ['fid'] },
    RegisterSalaryPaymentReply: { keyFields: ['paymentFid'] },
    SalaryPayment: { keyFields: ['fid'] },
    FoundingFormOwner: { keyFields: ['id'] },
    SubscriptionInformation: {
        keyFields: ['fid'],
        fields: { activeDiscounts: { merge: false } },
    },
    SubscriptionInvoice: {
        keyFields: ['fid'],
        fields: { appliedDiscounts: { merge: false } },
    },
    SubscriptionPreliminaryInvoice: {
        keyFields: false,
        fields: { discounts: { merge: false } },
    },
    BackofficeSearchHit: { keyFields: ['userFid'] },
    BackofficeOrganization: {
        keyFields: ['orgNumber'],
        fields: { subscriptionInformation: { merge: true } },
    },
    BackofficeAgent: { keyFields: ['fid'] },
    BackofficeFreeAgent: { keyFields: ['fid'] },
    BackofficeAccount: { keyFields: ['accountNumber'] },
    Broadcast: { keyFields: ['fid'] },
    Name: {
        keyFields: false,
        merge: true,
    },
    Versions: {
        keyFields: false,
        merge: true,
    },
    AccountingSystemInfo: { keyFields: false, merge: true },
    AmountInfo: { keyFields: false, merge: true },
    AgentAbstraction: { keyFields: false, merge: true },
    CrossborderPaymentAmlRecord: { keyFields: ['countryCode'] },
    AgentHint: { keyFields: false },
    CreatePaymentReply: { keyFields: false },
    CreateEmployeeReply: { keyFields: false },
    BankIdCodeReply: { keyFields: false },
    BankIdMobileReply: { keyFields: false },
    KidScheme: { keyFields: false },
    Autofill: { keyFields: false },
    UserPreferences: { keyFields: [] },
    StartAutofillReply: { keyFields: false },
    LedgerAccount: { keyFields: false, merge: true },
    AccountingSystemPaymentInformation: { keyFields: false, merge: true },
    BackofficeData: {
        // https://github.com/dotansimha/graphql-code-generator/pull/4979#issuecomment-716448711
        keyFields: false,
        fields: {
            organization(existingData, { args, toReference }) {
                if (existingData) {
                    return existingData;
                }
                // this should never happen...
                if (!args) {
                    return undefined;
                }
                return toReference({
                    __typename: 'BackofficeOrganization',
                    orgNumber: args.orgNumber,
                });
            },
            broadcast(existingData, { args, toReference }) {
                if (existingData) {
                    return existingData;
                }
                // this should never happen...
                if (!args) {
                    return undefined;
                }
                return toReference({
                    __typename: 'Broadcast',
                    fid: args.fid,
                });
            },
        },
    },
    Query: {
        // https://github.com/dotansimha/graphql-code-generator/pull/4979#issuecomment-716448711
        keyFields: false,
        fields: {
            employee(existingData, { args, toReference }) {
                if (existingData) {
                    return existingData;
                }
                // this should never happen...
                if (!args) {
                    return undefined;
                }
                return toReference({ __typename: 'Agent', fid: args.fid });
            },
            event(existingData, { args, toReference }) {
                if (existingData) {
                    return existingData;
                }
                // this should never happen...
                if (!args) {
                    return undefined;
                }
                return toReference({ __typename: 'Event', fid: args.fid });
            },
            payment(existingData, { args, toReference }) {
                if (existingData) {
                    return existingData;
                }
                // this should never happen...
                if (!args) {
                    return undefined;
                }
                return toReference({
                    __typename: 'PaymentInfo',
                    paymentFid: args.fid,
                });
            },
            salaryPayment(existingData, { args, toReference }) {
                if (existingData) {
                    return existingData;
                }
                // this should never happen...
                if (!args) {
                    return undefined;
                }
                return toReference({
                    __typename: 'SalaryPayment',
                    fid: args.fid,
                });
            },
            receipts(existingData, { args, toReference, canRead }) {
                if (existingData) {
                    return existingData;
                }
                // this should never happen...
                if (!args) {
                    return undefined;
                }
                const references = args.receiptFids.map(fid => toReference({ __typename: 'Receipt', fid }));
                if (references.every(canRead)) {
                    return references;
                }
                return undefined;
            },
            organization: { merge: true },
            simpleUserInfo: { merge: true },
            backofficeData: { merge: true },
            incompleteEvents: {
                merge(existing = [], incoming) {
                    const allEvents = new Map(
                    // we don't want to remove completed events from the data automatically, but we also need to make sure we
                    // don't have duplicates
                    [...existing, ...incoming].map(event => [event.__ref, event]));
                    return Array.from(allEvents.values());
                },
            },
            eventPage: {
                keyArgs: ['incompleteOnly', 'searchTerm'],
                merge(existing, incoming) {
                    var _a;
                    if (!existing) {
                        return incoming;
                    }
                    // `earliestTimestampInPage` must always be the earliest timestamp we've seen.
                    // If the incoming timestamp is later than what we already have in the cache,
                    // we must instead use the cached one.
                    // This can happen in this scenario:
                    // - Load the transactions page and scroll to load more events
                    // - Go to another page
                    // - Go back to the transactions page
                    // This will request the latest transactions again (without a `before`
                    // argument), and the `earliestTimestampInPage` in the response will
                    // be later than what we have in the cache.
                    const earliestTimestampInPage = incoming.earliestTimestampInPage < existing.earliestTimestampInPage
                        ? incoming.earliestTimestampInPage
                        : existing.earliestTimestampInPage;
                    // We only include stats in the first request, since it has some overhead.
                    // TODO: check that this always works
                    const incompleteCount = (_a = incoming.incompleteCount) !== null && _a !== void 0 ? _a : existing.incompleteCount;
                    return {
                        ...incoming,
                        earliestTimestampInPage,
                        incompleteCount,
                        combinedItems: uniqueRefs([
                            ...existing.combinedItems,
                            ...incoming.combinedItems,
                        ]),
                    };
                },
            },
        },
    },
};
function uniqueRefs(array) {
    // FIXME: check if the Apollo cache can be used in a typesafe manner
    return Array.from(new Set(array.filter(e => isReference(e)).map(e => e.__ref))).map(e => ({ __ref: e }));
}
// modified from https://github.com/apollographql/apollo-client/blob/11bac1604379b3278445c6290c75b3324bb2448b/src/link/batch-http/batchHttpLink.ts#L207-L219
export function batchHttpLinkBatchKey(operation) {
    const context = operation.getContext();
    const contextConfig = {
        http: context.http,
        options: context.fetchOptions,
        credentials: context.credentials,
        headers: {
            ...context.headers,
            [traceIdHeader]: '', // normalize this so that it can be batched
        },
    };
    return JSON.stringify(contextConfig);
}
export const fetchWithOperationNames = (url, options) => {
    // Try to extract the first operation name from the body. We handle the
    // cases where the body is a string, and where it's a FormData object with
    // an "operations" property that's a string. This is what is used for file
    // uploads in GraphQL
    try {
        let bodyString = undefined;
        if ((options === null || options === void 0 ? void 0 : options.body) instanceof FormData) {
            const entry = options.body.get('operations');
            if (typeof entry === 'string') {
                bodyString = entry;
            }
        }
        else if (typeof (options === null || options === void 0 ? void 0 : options.body) === 'string') {
            bodyString = options.body;
        }
        if (bodyString) {
            const parsedBody = JSON.parse(bodyString);
            const operationNames = [parsedBody]
                .flat()
                .map(operation => operation.operationName || 'unnamed')
                .join(',');
            return fetch(`${url}?${operationNames}`, options);
        }
    }
    catch (_a) {
        // should not happen, but just ignore if it does
    }
    return fetch(url, options);
};
const cacheConfig = { typePolicies };
export function createInMemoryCache() {
    // reset it each time a new cache is created
    expandedEvents(new Set());
    globalError(null);
    return new InMemoryCache(cacheConfig);
}
export { InMemoryCache };
function getGqlErrorExtensions(error, code) {
    return error.graphQLErrors
        .map(err => err.extensions)
        .filter(ext => ext.code === code);
}
export function getBankIdErrors(error) {
    const bankIdErrors = getGqlErrorExtensions(error, 'BankIdError');
    if (bankIdErrors.length === 0) {
        return undefined;
    }
    return bankIdErrors;
}
export function getFirstBankIdError(error) {
    const errors = getBankIdErrors(error);
    if (errors) {
        return errors[0];
    }
    return undefined;
}
export function getBackendErrors(error) {
    const backendErrors = getGqlErrorExtensions(error, 'BackendError');
    if (backendErrors.length === 0) {
        return undefined;
    }
    return backendErrors;
}
export function getFirstBackendErrors(error) {
    const errors = getBackendErrors(error);
    if (errors) {
        return errors[0];
    }
    return undefined;
}
export function getFirstBackendError(error) {
    const errors = getFirstBackendErrors(error);
    if (errors) {
        return errors.errors[0];
    }
    return undefined;
}
export function getOtherEmailsOfAgent(agent, newEmail) {
    const allEmails = agent.agents
        // remove current agent from list
        .filter(nestedAgent => nestedAgent.fid !== agent.fid)
        .map(agent => agent.email);
    return (Array.from(new Set(allEmails))
        .filter(notEmpty)
        // if what we're changing _to_ is the same as the current email, don't prompt
        .filter(email => email !== newEmail)
        .sort());
}
export function groupItemsByDate(items, dateProperty) {
    const itemsByDate = new Map();
    for (const item of items) {
        const groupDate = formatDateGroupHeader(new Date(item[dateProperty]));
        let dateArray = itemsByDate.get(groupDate);
        if (!dateArray) {
            dateArray = [];
            itemsByDate.set(groupDate, dateArray);
        }
        dateArray.push(item);
    }
    return itemsByDate;
}
export function groupItemsByDateNotFormatted(items, dateProperty) {
    const itemsByDate = new Map();
    for (const item of items) {
        const groupDate = item[dateProperty];
        let dateArray = itemsByDate.get(groupDate);
        if (!dateArray) {
            dateArray = [];
            itemsByDate.set(groupDate, dateArray);
        }
        dateArray.push(item);
    }
    return itemsByDate;
}
const defaultColors = {
    complete: colors.grayLightOpaque,
    incomplete: colors.grayShadow,
};
const movementColors = {
    Receive: {
        complete: colors.greenLight,
        incomplete: colors.green50Opaque,
    },
    Send: {
        complete: colors.redLight,
        incomplete: colors.red50Opaque,
    },
    Transfer: defaultColors,
    TransferFromSavings: defaultColors,
    TransferToSavings: defaultColors,
    TransferToCard: defaultColors,
    TransferToCardAutofill: defaultColors,
    TransferFromCard: defaultColors,
    TransferToTaxAccount: defaultColors,
    Unknown: defaultColors,
};
export const getMovementIconColors = (movementType = 'Unknown') => movementColors[movementType];
export const subscriptionTypeToLabel = {
    Mini: 'Mini',
    Standard: 'Standard',
    Advanced: 'Avansert',
};
export function useBatchedQuery(query, options) {
    return useQuery(query, {
        ...options,
        context: { ...options === null || options === void 0 ? void 0 : options.context, noBatch: false },
    });
}
export function useUnbatchedQuery(query, options) {
    return useQuery(query, {
        ...options,
        context: { ...options === null || options === void 0 ? void 0 : options.context, noBatch: true },
    });
}
export function getRandomAvatarId(existent) {
    const index = Math.floor(Math.random() * (availableAvatars.length - 1));
    const newAvatar = invariantValue(availableAvatars[index], `Unable to get avatar for index ${index}`);
    if (newAvatar !== existent) {
        return newAvatar;
    }
    return getRandomAvatarId(existent);
}
//# sourceMappingURL=index.js.map